import React, { useState } from 'react';
import { Button } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { useNavigate } from 'react-router-dom';
import { useShowMessage } from '../../../../../util/hooks';
import { getPolyglot } from '../../../../../i18n';
import { STModal } from '../../../../../components/commons/Modal';
import ButtonLoading from '../../../../../components/commons/ButtonLoading/ButtonLoading';
import { deleteGroup } from '../../../../../redux/groups/actions/thunks';
import { BusinessRole } from '../../../redux/businessModel/model/model';
import { GroupAPI } from '../../../../../redux/groups/api/GroupAPI';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { useGroupById } from '../../../../../redux/groups/hooks/useGroupById';
import StoerkIdAPI from '../../../../../redux/stoerkID/StoerkIdAPI';
import GroupModel from '../../../../../redux/groups/api/GroupModel';
import {
  getAllGroupsByWorkspaceIdSelector,
  getDictionaryGroups,
} from '../../../../../redux/groups/selectors';

interface GroupDeleteProps {
  showDeleteGroup: boolean;
  closeDeleteGroup: () => void;
  groupId: string;
}
/**
 * Group Delete
 * This class allow remove a group
 */
export function DeleteStoreModal(props: GroupDeleteProps) {
  const { closeDeleteGroup, groupId, showDeleteGroup } = props;
  const dispatch = useAppDispatch();
  const showErrorMessage = useShowErrorMessage();
  const navigate = useNavigate();
  const showMessage = useShowMessage();
  const polyglot = getPolyglot();
  const { data: group, isLoading: isGroupLoading } = useGroupById(groupId);

  const myWorkspace = useAppSelector(getMyWorkspaceSelector);
  const [getGroupsByWorkspace] = GroupAPI.useLazyGetGroupsByWorkspaceIdQuery();
  const [unlinkGroupToWorkspace] =
    StoerkIdAPI.useUnlinkGroupToWorkspaceMutation();
  const dictionaryGroups = useAppSelector(getDictionaryGroups);
  const dictionaryWorkspaceGroups = useAppSelector((state) =>
    getAllGroupsByWorkspaceIdSelector(state, {
      workspaceId: myWorkspace?.id ?? '',
    })
  );
  const childrenStores = GroupModel.getAllChildrenGroups(
    {
      ...dictionaryGroups,
      ...dictionaryWorkspaceGroups.reduce(
        (acc, group) => ({ ...acc, [group.id]: group }),
        {}
      ),
    },
    group?.childGroups ?? []
  );

  const [loading, setLoading] = useState(false);

  const isStore = group?.attributes?.BUSINESS_ROLE === BusinessRole.STORE;
  /**
   * Delete group
   * this function call the backend to remove a group
   */
  const onClickDeleteGroup = async () => {
    setLoading(true);
    try {
      if (!isStore) {
        await unlinkGroupToWorkspace({
          groupId,
          workspaceId: myWorkspace?.id ?? '',
        }).unwrap();
      }
      const childGroups = [
        ...childrenStores.map((child) => child.id),
        ...(group?.childGroups ?? []),
      ];
      if (childGroups && childGroups.length > 0) {
        for (const childGroup of childGroups) {
          await dispatch(deleteGroup(childGroup));
        }
      }

      await dispatch(deleteGroup(groupId));
      if (!isStore) {
        await getGroupsByWorkspace(myWorkspace?.id ?? '').unwrap();
      }
      /* show snack bar with successful message */
      closeDeleteGroup();
      navigate(`/storelist`);
      const message = isStore
        ? polyglot.t('freshwatch.store.delete_success')
        : polyglot.t('freshwatch.company.delete_success');
      showMessage(message);
    } catch (error) {
      showErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <STModal
      open={showDeleteGroup}
      title={
        isStore
          ? polyglot.t('freshwatch.store.delete')
          : polyglot.t('freshwatch.company.delete')
      }
      onClose={closeDeleteGroup}
      buttonActions={
        <>
          <Button onClick={closeDeleteGroup} variant="text">
            {polyglot.t('group.cancel_button_title')}
          </Button>
          <ButtonLoading
            variant="contained"
            color="error"
            onClick={onClickDeleteGroup}
            loading={loading || isGroupLoading}
          >
            {polyglot.t('group.delete_button_title')}
          </ButtonLoading>
        </>
      }
    >
      <p>
        {isStore
          ? polyglot.t('freshwatch.store.delete_message')
          : polyglot.t('freshwatch.company.delete_message')}
      </p>
      <p>
        {isStore
          ? polyglot.t('freshwatch.store.delete_message_2')
          : polyglot.t('freshwatch.company.delete_message_2')}
      </p>
    </STModal>
  );
}

export default DeleteStoreModal;
